import cx from 'classnames'
import { useEffect, useState, useCallback } from 'react'
import { Formik } from 'formik'

import Styles from './ChooseDestination.module.scss'

import { useModals } from 'almanac-ui/Modal'
import { useCreateFieldsetCompletion } from 'hooks/queries/workspaces/useCreateFieldsetCompletion'
import useResponsiveDesign from 'hooks/useResponsiveDesign'
import { multiStep } from 'components/Wizards/MultiStepWizard/multiStep'
import { showErrorMessageToast } from 'common/MessageToasts/ErrorMessageToast/ErrorMessageToast'

import { Button } from 'blaze-ui/Button'
import { Text } from 'blaze-ui/Text/Text'
import { Form } from '../../../Form/Form'
import { Heading } from 'blaze-ui/Heading/Heading'
import { Field } from 'components/Wizards/Wizard/Form/Field'
import { Field as BaseField } from 'components/Wizards/Field/Field'
import { StepProps } from 'components/Wizards/MultiStepWizard/Types'
import { Radio, RadioGroup } from 'components/RadioGroup'
import { RadioBullet } from 'components/RadioGroup/RadioBullet'
import { integrationToName } from 'components/IntegrationRequiredModal/IntegrationRequiredModal'
import { ExternalIntegration } from 'api/clients/ExternalIntegrationsClient'
import { PollFieldsetCompletion } from 'components/PollFieldsetCompletion/PollFieldsetCompletion'
import { AIJobStatus } from 'types'
import { GenerationType } from 'components/BlazeCommandLine/api/createDocGenerationRequest'
import { FilePathFormikField } from 'components/Wizards/FilePathFormikField/FilePathFormikField'

export enum Target {
  Create = 'create',
  Repurpose = 'repurpose',
}

const Option = ({
  selected,
  src,
  alt,
  header,
  subHeader,
  testId,
}: {
  selected: boolean
  src: string
  alt: string
  header: string
  subHeader: string
  testId: string
}) => {
  const { isMobile } = useResponsiveDesign()

  return (
    <div className={cx(Styles.option, { [Styles.selected]: selected })} data-testid={testId}>
      {selected && (
        <>
          <div className={Styles.selected} />
          {!isMobile && (
            <div className={Styles.radio}>
              <RadioBullet isSelected={selected} />
            </div>
          )}
        </>
      )}
      {!isMobile && (
        <div className={cx(Styles.topHalf, Styles.recommend)}>
          <img src={src} alt={alt} />
        </div>
      )}
      <div className={Styles.bottomHalf}>
        <Heading level={5}>{header}</Heading>
        <Text variant="secondary" color="var(--dark-60">
          {subHeader}
        </Text>
      </div>
    </div>
  )
}

type Props = StepProps & {
  chooseDestination: (values: any) => void
  externalIntegration: ExternalIntegration
  topic: string
}

export const ChooseDestination = multiStep(
  ({ schema, onSubmit, chooseDestination, externalIntegration, topic }: Props) => {
    const { closeModal } = useModals()
    const { meta } = schema.describe()
    const [target, setTarget] = useState<Target>(Target.Create)
    const [fieldsCompleted, setFieldsCompleted] = useState(false)
    const [isFieldsetError, setIsFieldsetError] = useState(false)
    const { mutate, data } = useCreateFieldsetCompletion()
    const filenameField = Object.entries(schema.fields).find(([name, _]) => name === 'project')
    const { isMobile } = useResponsiveDesign()

    const handleError = useCallback(() => {
      setIsFieldsetError(true)
      showErrorMessageToast(
        'Blaze was unable to generate the campaign name. Please contact support if this issue persists.',
      )
    }, [])

    // Generate and fetch the campaign name
    useEffect(() => {
      if (fieldsCompleted) return

      mutate(
        {
          fields: ['filename'],
          completedFields: {
            topic,
          },
          generationType: GenerationType.Campaign,
        },
        {
          onError: (error) => {
            reportError(error)
            handleError()
          },
        },
      )
    }, [fieldsCompleted, topic]) // eslint-disable-line react-hooks/exhaustive-deps

    return (
      <Formik
        validationSchema={schema}
        onSubmit={onSubmit}
        initialValues={schema.cast({ ...schema.getDefault() }, { assert: false })}
      >
        {({ values, isValid, submitForm, setFieldValue }) => {
          return (
            <>
              <PollFieldsetCompletion
                fieldsetCompletionId={data?.fieldset_completion.id}
                onComplete={(fieldset) => {
                  if (fieldset.status === AIJobStatus.Failed) {
                    handleError()
                    return
                  }

                  setFieldsCompleted(true)
                  setFieldValue('filename', fieldset.completed_fields['filename'])
                }}
              />
              <Form.Root>
                <Form.Section>
                  <div className={Styles.container}>
                    <div className={Styles.destination}>
                      <Text variant="largeList">Where do you want to post the new content?</Text>
                      <RadioGroup
                        defaultValue={Target.Create}
                        className={Styles.optionsContainer}
                        onChange={(value) => setTarget(value as Target)}
                        aria-label="Where do you want to post the new content?"
                      >
                        <Radio value={Target.Create} hideRadio aria-label="Recommend More Content">
                          {(props) => (
                            <Option
                              testId="create-more"
                              selected={props.isSelected}
                              src="https://res.cloudinary.com/almanac/image/upload/v1737125845/blaze_assets/recommend_more_content/recommed_more_fbkitr.png"
                              alt="Recommend more content"
                              header={`Create more ${integrationToName[externalIntegration.provider]} posts`}
                              subHeader="Generate new posts based on your winning content"
                            />
                          )}
                        </Radio>
                        <Radio value={Target.Repurpose} hideRadio aria-label="Repurpose Content">
                          {(props) => (
                            <Option
                              testId="repurpose-content"
                              selected={props.isSelected}
                              src="https://res.cloudinary.com/almanac/image/upload/v1737125845/blaze_assets/recommend_more_content/repurpose_same_pkmfcq.png"
                              alt="Repurpose content"
                              header="Repurpose post for another platform"
                              subHeader="Transform this content for other social channels"
                            />
                          )}
                        </Radio>
                      </RadioGroup>
                    </div>
                    {Object.entries(schema.fields)
                      .filter(([name]) => name !== 'project')
                      .map(([name, schema], i) => (
                        <Field key={name} name={name} schema={schema} autoFocus={i === 0} />
                      ))}
                    {filenameField && (
                      <BaseField label={filenameField[1].describe().meta.label} isRequired>
                        <FilePathFormikField
                          isNameLoading={!fieldsCompleted && !isFieldsetError}
                          placeholder={filenameField[1].describe().meta.placeholder}
                          locationPickerFullWidth={isMobile}
                          triggerComponent={isMobile ? 'formButton' : 'button'}
                          inputClassName={Styles.filenameInput}
                        />
                      </BaseField>
                    )}
                  </div>
                </Form.Section>
                <Form.Actions>
                  <Button variant="tertiary" onPress={closeModal}>
                    {meta.previousLabel}
                  </Button>
                  <Form.PrimaryActions isSticky>
                    <Button
                      aria-label={meta.submitLabel}
                      isDisabled={!isValid}
                      onPress={() => {
                        chooseDestination({ ...values, target })
                        if (target === Target.Create) {
                          submitForm()
                        }
                      }}
                    >
                      {meta.submitLabel}
                    </Button>
                  </Form.PrimaryActions>
                </Form.Actions>
              </Form.Root>
            </>
          )
        }}
      </Formik>
    )
  },
)
